import { IFilterConfig } from '@df/catalog-ui/listing/filter/filter';
import { FilterProperty } from '@df/catalog-ui/listing/filter/filter-property';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { Attribute } from '@df/catalog/attribute/attribute';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Model } from '@df/catalog/model';
import { IResourceIndex } from '@df/catalog/resource';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import keyBy from 'lodash-es/keyBy';
import reduce from 'lodash-es/reduce';
import uniq from 'lodash-es/uniq';

@ListingFilter({
    filterType: 'attribute-option'
})
export class FilterAttributeOption<
    TModel extends Model,
    TOption = Catalog.IAttributeOption | Catalog.IAttributeOptionGroup,
    TConfig extends IFilterConfig<string> = IFilterConfig<string>
> extends FilterProperty<TModel, number, TOption, TOption & { count: number }, TConfig> {
    /**
     * Attribute getter
     */
    get attribute(): Attribute {
        return this.app.resources.attribute.get(this.id);
    }

    getOptionsIndexed(): {
        [key: string]: TOption;
    } {
        if (!this._optionsIndexed) {
            this._optionsIndexed = keyBy(this.options, (option: TOption) => {
                if (this.useGroups) {
                    return (<Catalog.IAttributeOptionGroup>(<any>option)).values.join();
                }

                return `${(<Catalog.IAttributeOption>(<any>option)).value}`;
            });
        }

        return this._optionsIndexed;
    }

    getSelectionLabel() {
        const parts = this.attribute.getOptionLabel(this.selection || []);
        return Array.isArray(parts) ? parts.join() : parts;
    }

    getSelectedOptions(): Catalog.IAttributeOption[] {
        return (this.selection?.map(i => this.attribute.getOptionByValue(i)).filter(i => !!i) as Catalog.IAttributeOption[]) || [];
    }

    override getState(): (string | number)[] {
        return <(string | number)[]>compact(this._selection?.map(i => this.getOptionLabel(i))) || [];
    }

    override setState(state: (string | number)[]) {
        if (!state) {
            return;
        }
        if (!Array.isArray(state)) {
            state = [state];
        }
        this._selection = uniq(state.map(i => this.getOptionValue(i)).filter(i => i !== undefined)) as number[];
    }

    getOptionLabel(optionId: number): string | number {
        return this.attribute.getOptionLabel(optionId, <Catalog.IAttributeOption[]>(<any>this.options)) as string | number;
    }

    getOptionValue(optionLabel: string | number): number | undefined {
        return this.attribute.getOptionValue(optionLabel, <Catalog.IAttributeOption[]>(<any>this.options)) as number | undefined;
    }

    optionTrackByFn(index: number, option: Catalog.IAttributeOption) {
        return option.value;
    }

    /**
     * Gets index related to attribute from `Resource`
     */
    override getIndex(): IResourceIndex {
        return this.app.resources[this.listing.type].getIndex(this.id);
    }

    protected override toOptionWithCount(option: TOption, count: number): TOption & { count: number } {
        return {
            ...option,
            count
        };
    }

    /**
     * Counts matches from given source
     * (generalized to workwith both getCurrentMatchesCount and getMatchesCount)
     */
    protected override _getCount(source: { [key: string]: number }, option: TOption): number {
        if (option) {
            if (this.useGroups) {
                return reduce(
                    (<Catalog.IAttributeOptionGroup>(<any>option)).values,
                    (sum: number, value: number) => sum + source[value],
                    0
                );
            }
            return source[this.optionToId(option)] || 0;
        }
        return 0;
    }

    protected override optionToId(option: TOption): number {
        return (<Catalog.IAttributeOption>(<any>option)).value;
    }

    protected override getOptions(): TOption[] {
        return <TOption[]>(<any>(this.useGroups ? this.attribute.groups : this.attribute.data!.options)) || [];
    }

    protected override getSelectedValues(): number[] {
        const selection = this._selection || [];
        if (this.useGroups) {
            const options = <{ [key: string]: Catalog.IAttributeOptionGroup }>(<any>this.getOptionsIndexed());
            return flatten(selection.map(value => (options[value] ? options[value].values : [])));
        }

        return selection;
    }
}
